import React from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, Calendar, Shield, MessageSquare, FileText, CreditCard, Mail, 
  Users, CheckCircle2, Star, BrainCircuit, Activity, Smartphone, Printer,
  Database, Search, AlertCircle, TrendingUp, Bell, Headphones, Receipt,
  UserPlus, FileCheck, Stethoscope, History, Clock, Zap, Lock, Mic,
  FileBox, UserCheck, CalendarDays, Voicemail, Send, DollarSign, PieChart, 
  MousePointerClick, FileInput, BadgeDollarSign, HeartHandshake
} from 'lucide-react';

const row1 = [
  { icon: Phone, text: "Answer calls 24/7 with zero hold time" },
  { icon: Calendar, text: "Book appointments directly into Asha PMS" },
  { icon: Zap, text: "Fill last-minute schedule gaps instantly" },
  { icon: CheckCircle2, text: "Confirm tomorrow's appointments" },
  { icon: Activity, text: "Reactivate dormant hygiene patients" },
  { icon: MessageSquare, text: "Unified 2-way patient texting" },
  { icon: UserPlus, text: "Qualify & schedule new leads" },
  { icon: Clock, text: "Reschedule cancellations automatically" },
  { icon: Mic, text: "Transcribe patient calls in real-time" },
  { icon: Users, text: "Route emergency calls to on-call staff" },
  { icon: CalendarDays, text: "Send automated appointment reminders" },
  { icon: Voicemail, text: "Handle after-hours voicemails instantly" },
  { icon: HeartHandshake, text: "Answer patient FAQs intelligently" },
  { icon: UserCheck, text: "Detect new patient opportunities" },
];

const row2 = [
  { icon: Shield, text: "Verify insurance eligibility 2 days prior" },
  { icon: FileText, text: "Get full benefit breakdowns automatically" },
  { icon: FileCheck, text: "Write benefits back to patient file" },
  { icon: Search, text: "Scrub claims for coding errors" },
  { icon: Receipt, text: "Submit clean claims instantly" },
  { icon: AlertCircle, text: "Predict & prevent claim denials" },
  { icon: Headphones, text: "Handle insurance payer phone calls" },
  { icon: History, text: "Generate clinical narratives for claims" },
  { icon: Stethoscope, text: "Check remaining deductibles & max" },
  { icon: Lock, text: "Detect missing attachments before sending" },
  { icon: FileBox, text: "Identify procedure-specific limitations" },
  { icon: BrainCircuit, text: "Automate pre-authorization requests" },
  { icon: FileInput, text: "Digitize paper EOBs automatically" },
  { icon: BadgeDollarSign, text: "Calculate accurate patient copays" },
];

const row3 = [
  { icon: Star, text: "Generate 5-star Google reviews" },
  { icon: Database, text: "Post EOBs & reconcile payments" },
  { icon: TrendingUp, text: "Collect patient balances via text" },
  { icon: AlertCircle, text: "Fight denied claims with AI appeals" },
  { icon: Printer, text: "Auto-sort & file incoming faxes" },
  { icon: Smartphone, text: "Send mass SMS marketing campaigns" },
  { icon: Mail, text: "Launch email newsletters to patients" },
  { icon: CreditCard, text: "Process text-to-pay transactions" },
  { icon: FileText, text: "Send digital intake forms pre-visit" },
  { icon: Bell, text: "Automate post-op checkup calls" },
  { icon: PieChart, text: "Track practice ROI in real-time" },
  { icon: Send, text: "Blast seasonal promotions to patients" },
  { icon: DollarSign, text: "Identify unscheduled treatment plans" },
  { icon: MousePointerClick, text: "Enable online booking for patients" },
];

const FeatureCard = ({ item }) => (
  <div className="flex items-center gap-3 px-5 py-3.5 bg-white rounded-2xl border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.08)] hover:border-gray-200 hover:-translate-y-0.5 transition-all duration-300 w-max group cursor-default">
    <div className="w-9 h-9 rounded-xl bg-gray-50 flex items-center justify-center shrink-0 group-hover:bg-blue-50 transition-colors duration-300">
      <item.icon className="w-4.5 h-4.5 text-gray-400 group-hover:text-blue-600 transition-colors duration-300" />
    </div>
    <span className="text-sm font-medium text-gray-600 whitespace-nowrap group-hover:text-gray-900 transition-colors duration-300">{item.text}</span>
  </div>
);

const MarqueeRow = ({ items, direction = "left", speed = 50 }) => (
  <div className="relative flex overflow-hidden py-2">
    <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white via-white/80 to-transparent z-10 pointer-events-none" />
    <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white via-white/80 to-transparent z-10 pointer-events-none" />
    <motion.div 
      className="flex gap-4 px-2"
      initial={{ x: direction === "left" ? 0 : "-50%" }}
      animate={{ x: direction === "left" ? "-50%" : 0 }}
      transition={{ duration: speed, ease: "linear", repeat: Infinity }}
    >
      {[...items, ...items, ...items, ...items].map((item, idx) => (
        <FeatureCard key={`${item.text}-${idx}`} item={item} />
      ))}
    </motion.div>
  </div>
);

const EcosystemSection = () => {
  return (
    <section className="py-28 bg-white relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-100 mb-6"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
            </span>
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-widest">End-to-End Automation</span>
          </motion.div>
          
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 tracking-tight leading-[1.1]"
          >
            <span className="text-blue-600">20+ Dental AI Products</span><br />
            in One Platform.
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed"
          >
            So what all can you do with Asha's Comprehensive AI Platform?{' '}
            <span className="text-gray-700 font-medium">Here are just a few examples.</span>
          </motion.p>
        </div>

        <div className="space-y-3 -mx-4 md:-mx-12">
          <MarqueeRow items={row1} direction="left" speed={300} />
          <MarqueeRow items={row2} direction="right" speed={350} />
          <MarqueeRow items={row3} direction="left" speed={400} />
        </div>

        <div className="mt-14 text-center">
          <p className="text-sm font-semibold text-gray-300 uppercase tracking-widest">And dozens more capabilities...</p>
        </div>
      </div>
    </section>
  );
};

export default EcosystemSection;
